import React, { useState, useEffect } from 'react';
import { restClient, EventType } from '../../../api/rest-client';
import { ParticipationData, ParticipationEvent } from '../../../types/participation';
import ParticipationEventEntry from './ParticipationEventEntry';

interface ParticipationHistoryCardProps {
  riderId: string;
}

/**
 * ParticipationHistoryCard Component
 * 
 * Displays a rider's participation history with:
 * - Season and event type filters
 * - Summary statistics (total events, total hours)
 * - List of participation events
 * - Empty state when no data
 * - Error handling
 */
export const ParticipationHistoryCard: React.FC<ParticipationHistoryCardProps> = ({ riderId }) => {
  const [participationData, setParticipationData] = useState<ParticipationData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedSeason, setSelectedSeason] = useState<string>('');
  const [selectedEventType, setSelectedEventType] = useState<string>('all');
  const [eventTypes, setEventTypes] = useState<EventType[]>([]);
  const [expanded, setExpanded] = useState(false);
  const COLLAPSE_THRESHOLD = 10;
  
  // Fetch event types for color mapping
  useEffect(() => {
    const fetchEventTypes = async () => {
      try {
        const types = await restClient.getEventTypes();
        setEventTypes(types);
      } catch (error) {
        // Silent error handling - will use default colors
      }
    };
    fetchEventTypes();
  }, []);

  /**
   * Get color for event type
   */
  const getEventTypeColor = (eventTypeName: string): string => {
    const eventType = eventTypes.find(et => et.name === eventTypeName);
    return eventType?.color || '#435d7d'; // Default to primary blue
  };
  
  // Fetch participation data
  const fetchParticipationData = async (season?: string) => {
    setLoading(true);
    setError(null);
    
    try {
      const data = await restClient.getRiderParticipation(riderId, season);
      setParticipationData(data);
      
      // Set default season to current year if not already set
      if (!selectedSeason && data.available_seasons.length > 0) {
        const currentYear = new Date().getFullYear().toString();
        const defaultSeason = data.available_seasons.includes(currentYear) 
          ? currentYear 
          : data.available_seasons[0]; // Fallback to first available season
        setSelectedSeason(defaultSeason);
      }
    } catch (err) {
      console.error('Failed to fetch participation data:', err);
      setError('Failed to load participation history. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  // Initial load - fetch all seasons first to get available seasons
  useEffect(() => {
    fetchParticipationData(undefined);
  }, [riderId]);
  
  // Fetch data for selected season when it changes (but not on initial empty state)
  useEffect(() => {
    if (selectedSeason && selectedSeason !== 'all') {
      fetchParticipationData(selectedSeason);
    }
  }, [selectedSeason]);
  
  // Handle season change
  const handleSeasonChange = (season: string) => {
    setSelectedSeason(season);
    setExpanded(false);
    fetchParticipationData(season === 'all' ? undefined : season);
  };
  
  // Handle event type change (client-side filtering)
  const handleEventTypeChange = (eventType: string) => {
    setSelectedEventType(eventType);
    setExpanded(false);
  };
  
  // Get filtered events based on selected event type
  const getFilteredEvents = (): ParticipationEvent[] => {
    if (!participationData) return [];
    
    if (selectedEventType === 'all') {
      return participationData.events;
    }
    
    return participationData.events.filter(
      event => event.event_type === selectedEventType
    );
  };
  
  // Calculate statistics for filtered events
  const calculateFilteredStats = () => {
    const filteredEvents = getFilteredEvents();
    const totalEvents = filteredEvents.length;
    const totalHours = filteredEvents.reduce(
      (sum, event) => sum + event.duration_hours,
      0
    );
    
    return {
      totalEvents,
      totalHours: totalHours.toFixed(1)
    };
  };
  
  // Get unique event types from data
  const getAvailableEventTypes = (): string[] => {
    if (!participationData) return [];
    
    const types = new Set(participationData.events.map(event => event.event_type));
    return Array.from(types).sort();
  };
  
  // Loading state
  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-md p-4 sm:p-6">
        <h2 className="text-xl sm:text-2xl font-bold text-gray-800 mb-4">Participation History</h2>
        <div className="animate-pulse">
          <div className="h-10 bg-gray-200 rounded mb-4"></div>
          <div className="h-10 bg-gray-200 rounded mb-4"></div>
          <div className="h-32 bg-gray-200 rounded mb-4"></div>
          <div className="h-32 bg-gray-200 rounded mb-4"></div>
        </div>
      </div>
    );
  }
  
  // Error state
  if (error) {
    return (
      <div className="bg-white rounded-lg shadow-md p-4 sm:p-6">
        <h2 className="text-xl sm:text-2xl font-bold text-gray-800 mb-4">Participation History</h2>
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center">
          <p className="text-red-600 mb-2">{error}</p>
          <button
            onClick={() => fetchParticipationData(selectedSeason === 'all' ? undefined : selectedSeason)}
            className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }
  
  // Empty state
  if (!participationData || participationData.events.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-md p-4 sm:p-6">
        <h2 className="text-xl sm:text-2xl font-bold text-gray-800 mb-4">Participation History</h2>
        
        {/* Season filter */}
        {participationData && participationData.available_seasons.length > 0 && (
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Season
            </label>
            <select
              value={selectedSeason}
              onChange={(e) => handleSeasonChange(e.target.value)}
              className="w-full md:w-64 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Seasons</option>
              {participationData.available_seasons.map(season => (
                <option key={season} value={season}>{season}</option>
              ))}
            </select>
          </div>
        )}
        
        <div className="text-center py-12">
          <div className="text-6xl mb-4">📅</div>
          <h3 className="text-xl font-semibold text-gray-700 mb-2">
            No participation history found
          </h3>
          <p className="text-gray-500">
            This rider hasn't attended any events
            {selectedSeason && selectedSeason !== 'all' ? ` in ${selectedSeason}` : ''}.
          </p>
        </div>
      </div>
    );
  }
  
  const filteredEvents = getFilteredEvents();
  const stats = calculateFilteredStats();
  const availableEventTypes = getAvailableEventTypes();
  
  return (
    <div className="bg-white rounded-lg shadow-md p-4 sm:p-6">
      <h2 className="text-xl sm:text-2xl font-bold text-gray-800 mb-4">Participation History</h2>
      
      {/* Filters */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        {/* Season filter */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Season
          </label>
          <select
            value={selectedSeason}
            onChange={(e) => handleSeasonChange(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            disabled={loading}
          >
            <option value="all">All Seasons</option>
            {participationData.available_seasons.map(season => (
              <option key={season} value={season}>{season}</option>
            ))}
          </select>
        </div>
        
        {/* Event type filter */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Event Type
          </label>
          <select
            value={selectedEventType}
            onChange={(e) => handleEventTypeChange(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">All Types</option>
            {availableEventTypes.map(type => (
              <option key={type} value={type}>{type}</option>
            ))}
          </select>
        </div>
      </div>
      
      {/* Summary statistics */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 sm:p-4 mb-6">
        <div className="text-center">
          <span className="text-base sm:text-lg font-semibold text-blue-900 break-words">
            {stats.totalEvents} events • {stats.totalHours} hours
          </span>
        </div>
      </div>
      
      {/* Event list */}
      {filteredEvents.length > 0 ? (
        <div>
          {(filteredEvents.length > COLLAPSE_THRESHOLD && !expanded
            ? filteredEvents.slice(0, COLLAPSE_THRESHOLD)
            : filteredEvents
          ).map(event => (
            <ParticipationEventEntry 
              key={event.event_id} 
              event={event} 
              eventTypeColor={getEventTypeColor(event.event_type)}
            />
          ))}
          {filteredEvents.length > COLLAPSE_THRESHOLD && (
            <button
              onClick={() => setExpanded(!expanded)}
              aria-expanded={expanded}
              className="mt-3 w-full text-center py-2 text-sm font-medium text-primary-700 hover:text-primary-900 hover:bg-primary-100 rounded-lg transition-colors"
            >
              {expanded ? 'Show top 10 only' : `Show all ${filteredEvents.length} events`}
            </button>
          )}
        </div>
      ) : (
        <div className="text-center py-8 text-gray-500">
          No events found for the selected filters.
        </div>
      )}
    </div>
  );
};

export default ParticipationHistoryCard;
